import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ROLE_RANK, useAuth } from "./AuthContext";
import type { Role } from "./AuthContext";
import { MENU_ROLE_MANIFEST } from "../../data/menuRoleManifest";
import type { MenuOverride } from "../../data/menuOverrides";
import { homeAreaOf, makeMenuPlacement } from "../../data/menuPlacement";
import { isAlwaysVisibleSettingsMenu, isSettingsMenuKey, resolveSettingsEnabled, resolveSettingsRole, settingsCodeParent } from "../../data/settingsMenus";

type MenuRoleMapping = {
  menu_key: string;
  min_role: Role;
  updated_by: string;
  updated_at: string;
  /** 메뉴 사용 여부. 마이그레이션 전에는 서버가 이 필드를 주지 않으므로 true 로 본다. */
  enabled: boolean;
};

type MenuRoleContextValue = {
  /** DB 매핑 원본 (관리 UI용) */
  mappings: Record<string, MenuRoleMapping>;
  loaded: boolean;
  /** 이 메뉴에 직접 설정된 최소 role (DB 매핑 > 코드 기본값 > fallback). 상속 미적용 — 관리 UI 현재값 표시용. */
  getMinRole: (menuKey: string, fallback?: Role) => Role;
  /** 상속 적용 최소 role — 상위 메뉴가 최저 권한(floor)이 된다. 노출·접근 판정은 이 값 기준. */
  getEffectiveMinRole: (menuKey: string, fallback?: Role) => Role;
  /** 부모 메뉴의 상속 최소 role — 관리 UI에서 자식 선택 하한으로 사용. 부모 없으면 guest. */
  getParentMinRole: (menuKey: string) => Role;
  /** 이 메뉴에 직접 설정된 사용 여부 (상속 미적용). 관리 UI 체크박스 현재값. */
  isEnabled: (menuKey: string) => boolean;
  /** 상속 적용 사용 여부 — 자신 또는 조상 중 하나라도 미사용이면 false. 노출·접근 판정은 이 값. */
  getEffectiveEnabled: (menuKey: string) => boolean;
  /** 조상 중 미사용이 있는가 — 관리 UI "상위 미사용" 표시용. */
  isDisabledByAncestor: (menuKey: string) => boolean;
  /** 현재 유저에게 이 메뉴가 노출/접근 가능한가 (상속 적용). 미사용이면 super_admin 에게도 false. */
  visible: (menuKey: string, fallback?: Role) => boolean;
  /** 임의의 등급으로 본 노출 여부 — visible() 과 같은 판정. 관리 화면의 "권한별 미리보기"용.
   *  visible() 이 이 함수에 현재 role 을 넘기는 구조라, 미리보기가 실제와 어긋날 수 없다. */
  visibleFor: (asRole: Role, menuKey: string, fallback?: Role) => boolean;
  /** 메뉴 구조 오버라이드(menu_items). 비어 있으면 코드 기본 구조 그대로다. */
  overrides: MenuOverride[];
  refresh: () => Promise<void>;
};

const MenuRoleContext = createContext<MenuRoleContextValue | null>(null);

function isRole(value: unknown): value is Role {
  return value === "super_admin" || value === "admin" || value === "viewer" || value === "guest";
}

const RANK_TO_ROLE: Role[] = ["guest", "viewer", "admin", "super_admin"];

// 코드 기본값 맵 (매니페스트) — 상속 계산 시 조상들의 기본값을 알아야 하므로 여기서 참조.
const DEFAULT_ROLE_BY_KEY: Record<string, Role> = MENU_ROLE_MANIFEST.reduce((acc, e) => {
  acc[e.key] = e.defaultRole;
  return acc;
}, {} as Record<string, Role>);

// 메뉴 키의 코드 기본 부모. topnav이 최상위, 그 아래 섹션, 그 아래 항목.
//  crm:{s}:{i} → crm:{s} → topnav:CRM
//  automation:{s}:{i} → automation:{s} → topnav:IT-CONSULTING
function codeParentKeyOf(key: string): string | null {
  const p = key.split(":");
  if (p[0] === "topnav") return null;
  // 설정 메뉴: 항목 → 섹션(settings:account / settings:admin) → 없음. 카탈로그가 정한다.
  if (p[0] === "settings") return settingsCodeParent(key);
  if (p[0] === "crm") return p.length >= 3 ? `crm:${p[1]}` : "topnav:CRM";
  if (p[0] === "automation") return p.length >= 3 ? `automation:${p[1]}` : "topnav:IT-CONSULTING";
  return null;
}

// 메뉴 구조 편집에서 소속을 옮겼으면 그 부모를 따른다 — 권한 하한·사용 여부 상속이
// 실제로 붙어 있는 자리 기준이어야 화면과 어긋나지 않는다.
// 직접 추가한 메뉴(custom:*)는 키에서 부모를 알 수 없으므로 오버라이드가 유일한 근거다.
// 섹션을 다른 대메뉴로 옮겼으면(menuPlacement) 그 대메뉴의 상단 네비가 부모다 —
// IT-CONSULTING 으로 옮긴 CRM 섹션은 topnav:IT-CONSULTING 의 권한·사용 여부를 물려받는다.
function makeParentKeyOf(overrides: MenuOverride[]): (key: string) => string | null {
  const parentByKey = new Map<string, string | null>();
  const placement = makeMenuPlacement(overrides);
  overrides.forEach((o) => {
    if (o.parent_key !== null && o.parent_key !== undefined) parentByKey.set(o.menu_key, o.parent_key);
    else if (o.is_custom) parentByKey.set(o.menu_key, null);
    else if (o.menu_key.split(":").length === 2 && homeAreaOf(o.menu_key)) {
      const placed = placement.sectionArea(o.menu_key);
      if (placed && placed !== homeAreaOf(o.menu_key)) {
        parentByKey.set(o.menu_key, placed === "crm" ? "topnav:CRM" : "topnav:IT-CONSULTING");
      }
    }
  });
  return (key: string) => (parentByKey.has(key) ? parentByKey.get(key)! : codeParentKeyOf(key));
}

/**
 * 메뉴↔role DB 매핑(menu_role_map) 컨텍스트.
 * 메뉴 구조·라벨은 코드 소유, DB는 "menu_key → min_role" 오버라이드.
 * 상위 메뉴의 최소 role이 하위의 하한(floor) — 상위가 admin이면 하위는 admin 미만이 될 수 없다.
 * 로드 실패·매핑 없음 → 코드 기본값 폴백. AuthProvider 하위 필수.
 */
export function MenuRoleProvider({ children }: { children: React.ReactNode }) {
  const { status, role } = useAuth();
  const [mappings, setMappings] = useState<Record<string, MenuRoleMapping>>({});
  const [overrides, setOverrides] = useState<MenuOverride[]>([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    // 두 가지를 한 번에 읽는다: 권한 매핑(menu_role_map) + 구조 오버라이드(menu_items).
    // 각각 독립적으로 실패해도 나머지는 살아야 한다 → 개별 try 로 감싼다.
    const roles = fetch("/api/auth-users/menu-roles", { credentials: "include" })
      .then((res) => { if (!res.ok) throw new Error(`${res.status}`); return res.json(); })
      .then((data) => {
        const next: Record<string, MenuRoleMapping> = {};
        (data.mappings || []).forEach((m: Partial<MenuRoleMapping>) => {
          if (m.menu_key && isRole(m.min_role)) {
            // enabled 가 없으면(마이그레이션 전 서버) 사용 중으로 본다 — 기능이 조용히 꺼지면 안 된다.
            next[m.menu_key] = { ...(m as MenuRoleMapping), enabled: m.enabled !== false };
          }
        });
        setMappings(next);
      })
      .catch(() => setMappings({}));

    // 구조 오버라이드는 없으면 코드 기본값 — 실패해도 사이드바는 그대로 뜬다.
    const items = fetch("/api/auth-users/menu-items", { credentials: "include" })
      .then((res) => { if (!res.ok) throw new Error(`${res.status}`); return res.json(); })
      .then((data) => setOverrides(Array.isArray(data.items) ? data.items : []))
      .catch(() => setOverrides([]));

    await Promise.all([roles, items]);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (status === "authenticated") {
      refresh();
    } else if (status === "unauthenticated") {
      setMappings({});
      setOverrides([]);
      setLoaded(true);
    }
  }, [status, refresh]);

  const value = useMemo<MenuRoleContextValue>(() => {
    // 상속 계산의 부모 = 오버라이드가 있으면 그 부모, 없으면 코드 기본 부모.
    const parentKeyOf = makeParentKeyOf(overrides);
    // 이 키에 직접 설정된 role (상속 미적용)
    // 설정 관리자 메뉴(settings:*)는 기본값이 메뉴마다 다르다(settingsMenus.ts) — 매니페스트 대신 그 값을 쓴다.
    const ownRole = (menuKey: string, fallback: Role = "viewer"): Role =>
      isSettingsMenuKey(menuKey)
        ? resolveSettingsRole(menuKey, mappings[menuKey]?.min_role)
        : mappings[menuKey]?.min_role ?? DEFAULT_ROLE_BY_KEY[menuKey] ?? fallback;

    const getMinRole = (menuKey: string, fallback: Role = "viewer"): Role => ownRole(menuKey, fallback);

    // 상속: 자신 + 모든 조상의 own role 중 가장 높은 등급
    const getEffectiveMinRole = (menuKey: string, fallback: Role = "viewer"): Role => {
      let rank = ROLE_RANK[ownRole(menuKey, fallback)];
      // 부모가 DB 값이라 이론상 순환이 가능하다 — 방문 집합으로 무한 루프를 막는다.
      const seen = new Set<string>([menuKey]);
      let cur = parentKeyOf(menuKey);
      while (cur && !seen.has(cur)) {
        seen.add(cur);
        rank = Math.max(rank, ROLE_RANK[ownRole(cur)]);
        cur = parentKeyOf(cur);
      }
      return RANK_TO_ROLE[rank];
    };

    const getParentMinRole = (menuKey: string): Role => {
      const parent = parentKeyOf(menuKey);
      return parent ? getEffectiveMinRole(parent) : "guest";
    };

    // ── 사용/미사용 — role 보다 상위 개념 ──────────────────────────────────
    // 행이 없으면 사용. 코드 매니페스트에는 사용 여부 기본값이 없다(전부 사용이 기본).
    // 설정 메뉴 중 메뉴 권한 관리는 끌 수 없다 — 꺼지면 되돌릴 화면이 없다(resolveSettingsEnabled).
    const isEnabled = (menuKey: string): boolean =>
      isSettingsMenuKey(menuKey)
        ? resolveSettingsEnabled(menuKey, mappings[menuKey]?.enabled)
        : mappings[menuKey]?.enabled !== false;

    // 상속: 자신 또는 조상 중 하나라도 미사용이면 미사용.
    const getEffectiveEnabled = (menuKey: string): boolean => {
      // 메뉴 권한 관리·계정 설정은 조상 섹션이 꺼져도 보인다 — 자물쇠 방지.
      if (isAlwaysVisibleSettingsMenu(menuKey)) return true;
      if (!isEnabled(menuKey)) return false;
      return !isDisabledByAncestor(menuKey);
    };

    const isDisabledByAncestor = (menuKey: string): boolean => {
      const seen = new Set<string>([menuKey]);
      let cur = parentKeyOf(menuKey);
      while (cur && !seen.has(cur)) {
        seen.add(cur);
        if (!isEnabled(cur)) return true;
        cur = parentKeyOf(cur);
      }
      return false;
    };

    // 판정 본체 — "누가" 보느냐만 인자로 받는다. 현재 유저용 visible() 과 관리 화면의
    // 권한별 미리보기가 같은 함수를 타야 미리보기가 실제 화면과 어긋나지 않는다.
    const visibleFor = (asRole: Role, menuKey: string, fallback: Role = "viewer"): boolean => {
      // 미사용이 role 판정보다 먼저다. super_admin 예외도 여기엔 적용되지 않는다 —
      // "미사용"은 등급이 하나 더 생긴 게 아니라 조직 차원에서 메뉴를 끈 것이다.
      // 자물쇠 위험 없음: /admin 진입은 visible() 이 아니라 hasRole("admin") 로 게이팅된다.
      if (!getEffectiveEnabled(menuKey)) return false;
      if (asRole === "super_admin") return true;
      return ROLE_RANK[asRole] >= ROLE_RANK[getEffectiveMinRole(menuKey, fallback)];
    };

    const visible = (menuKey: string, fallback: Role = "viewer"): boolean =>
      visibleFor(role ?? "viewer", menuKey, fallback);

    return {
      mappings,
      loaded,
      getMinRole,
      getEffectiveMinRole,
      getParentMinRole,
      isEnabled,
      getEffectiveEnabled,
      isDisabledByAncestor,
      visible,
      visibleFor,
      overrides,
      refresh,
    };
  }, [mappings, overrides, loaded, role, refresh]);

  return <MenuRoleContext.Provider value={value}>{children}</MenuRoleContext.Provider>;
}

export function useMenuRoles(): MenuRoleContextValue {
  const context = useContext(MenuRoleContext);
  if (!context) throw new Error("useMenuRoles는 MenuRoleProvider 하위에서만 사용할 수 있습니다.");
  return context;
}
